/**
 * useAuth Hook
 * 
 * Custom hook for managing authentication state.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  isAuthenticated as checkAuth,
  logout as logoutService,
} from '../features/auth/services/auth';

interface UseAuthReturn {
  authenticated: boolean;
  loading: boolean;
  handleLoginSuccess: () => void;
  handleLogout: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = useCallback(() => {
    const isAuth = checkAuth();
    setAuthenticated(isAuth);
    setLoading(false);
  }, []);

  const handleLoginSuccess = useCallback(() => {
    setAuthenticated(true);
  }, []);

  const handleLogout = useCallback(async () => {
    await logoutService();
    setAuthenticated(false);
  }, []);

  useEffect(() => {
    checkAuthStatus();

    const interval = setInterval(() => {
      if (authenticated) {
        checkAuthStatus();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [authenticated, checkAuthStatus]);

  return {
    authenticated,
    loading,
    handleLoginSuccess,
    handleLogout,
  };
};

export default useAuth;
